import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ArticleShell, { Ext } from "@/components/content/ArticleShell";

export const MEDICARE_TEST = "pdx-medicare-h1-2026-11";
type V = "A" | "B";
const KEY = "px_ab_medicare";
// Copy-cleared (medicare-glp1-coverage-faq-2026-11-03.COPY-CLEARED.md)
const META_TITLE_A = "Medicare GLP-1 Bridge: $50 a Month, Who's Eligible";
const H1: Record<V, string> = {
  A: "The Medicare GLP-1 Bridge: a $50 copay, and who's eligible",
  B: "Does Medicare cover GLP-1s for weight loss? What changed in 2026",
};
const SUBHEAD = "What you'd pay, which medicines are included, who's eligible, and when the program ends.";
const COST = "/glp-1-cost";
type Gtag = (...a: unknown[]) => void;
const gtag = (): Gtag | undefined => (window as unknown as { gtag?: Gtag }).gtag;

function useMedicareVariant(): V {
  const [v, setV] = useState<V>("A");
  useEffect(() => {
    let x: V;
    try {
      const s = window.localStorage.getItem(KEY);
      x = s === "A" || s === "B" ? s : Math.random() < 0.5 ? "A" : "B";
      window.localStorage.setItem(KEY, x);
    } catch { x = Math.random() < 0.5 ? "A" : "B"; }
    setV(x);
    const g = gtag();
    if (typeof g === "function") g("event", "ab_impression", { test_name: MEDICARE_TEST, variant: x, source: "medicare-glp-1-coverage" });
  }, []);
  return v;
}

// Visible FAQ and FAQPage JSON-LD both render from this array (word for word).
const faqs = [
  {
    question: "What does a GLP-1 cost with the Medicare GLP-1 Bridge?",
    answer: "Under the Medicare GLP-1 Bridge, eligible people pay a $50 copayment for a one-month supply (28 or 30 days, depending on the drug), per CMS, checked Nov 3, 2026. The Part D deductible doesn't apply, the copay doesn't count toward your Part D out-of-pocket limit, and Extra Help can't lower it, per CMS, checked Nov 3, 2026.",
  },
  {
    question: "Does Medicare cover GLP-1 medicines for weight loss?",
    answer: "Only through a temporary program, and only for people who meet its criteria. The Medicare GLP-1 Bridge is a short-term CMS demonstration for certain people with Medicare drug coverage (Part D). It runs from July 1, 2026 through December 31, 2027, per CMS, checked Nov 3, 2026. It works outside regular Part D coverage.",
  },
  {
    question: "Which medicines does the Medicare GLP-1 Bridge cover?",
    answer: "Foundayo (tablet), Wegovy (injection or tablet) and Zepbound KwikPen only, per CMS, checked Nov 3, 2026. Zepbound single-dose vials and single-dose pens are not covered by the program. CMS says the list may change.",
  },
  {
    question: "Who is eligible for the Medicare GLP-1 Bridge?",
    answer: "Medicare lists the criteria: you must have Medicare drug coverage (Part D), be 18 or older, and meet a body mass index (BMI) requirement when you start GLP-1 therapy, with some BMI levels also requiring certain health conditions. Your provider must send the prescription and, when requested, complete a prior authorization. Only Medicare and your provider can confirm eligibility; see Medicare.gov/glp1bridge for the full criteria.",
  },
  {
    question: "Who is not eligible for the Medicare GLP-1 Bridge?",
    answer: "Medicare says people who already get GLP-1 drugs covered through their Part D plan aren't eligible, and neither are people with type 2 diabetes, moderate-to-severe sleep apnea or fatty liver disease. For those conditions, Medicare says your Part D plan might cover a GLP-1. Check with your plan.",
  },
  {
    question: "What happens after 2027?",
    answer: "CMS hasn't announced coverage after December 31, 2027. It extended the Bridge through that date after a planned longer-term program, the BALANCE Model, did not launch in Part D in 2027.",
  },
];

const SOURCES = [
  { label: "Medicare.gov, Weight loss drugs", href: "https://www.medicare.gov/coverage/weight-loss-drugs" },
  { label: "CMS, Medicare GLP-1 Bridge", href: "https://www.cms.gov/medicare/coverage/prescription-drug-coverage/medicare-glp-1-bridge" },
  { label: "CMS, Information for Part D Plans", href: "https://www.cms.gov/medicare/coverage/prescription-drug-coverage/medicare-glp-1-bridge/information-part-d-plans" },
];

export default function MedicareGlp1Coverage() {
  const variant = useMedicareVariant();
  const onCost = () => {
    const g = gtag();
    if (typeof g === "function") g("event", "cost_click", { test_name: MEDICARE_TEST, variant, source: "medicare-glp-1-coverage", target: COST });
  };
  return (
    <ArticleShell
      kicker="Coverage"
      title={META_TITLE_A}
      heading={H1[variant]}
      description={SUBHEAD}
      path="/medicare-glp-1-coverage"
      lastChecked="Nov 3, 2026"
      lastCheckedIso="2026-11-03"
      faqs={faqs}
    >
      <p className="text-base">{SUBHEAD}</p>
      {faqs.map(f => (
        <section key={f.question}>
          <h2 className="text-xl font-bold text-foreground mb-2">{f.question}</h2>
          <p>{f.answer}</p>
        </section>
      ))}
      <div>
        <p className="font-semibold text-foreground">Sources (all checked Nov 3, 2026):</p>
        <ul className="list-disc pl-5">
          {SOURCES.map(s => (<li key={s.href}><Ext href={s.href}>{s.label}</Ext></li>))}
        </ul>
      </div>
      <p className="text-xs">Related: <Link to={COST} onClick={onCost} className="text-primary hover:underline">GLP-1 cost guide</Link></p>
      <p className="text-xs text-muted-foreground">PeptideX is an independent educational site and is not affiliated with or endorsed by Medicare, CMS or any government agency. This page is educational information, not medical or coverage advice. Program details can change; check Medicare.gov for current rules.</p>
    </ArticleShell>
  );
}
